import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Globe, Trash2, X, ExternalLink, Download, StickyNote, Flame, LineChart } from 'lucide-react';
import { apiGet, apiPost, apiDel } from '../api';
import { computeMetrics, WritingActivity, WritingMetrics } from '../utils/writingMetrics';
import { Book, Revision, Series } from '../types/api';
import { bareId } from '../entityIds';
import DesktopAppNote from '../components/DesktopAppNote';
import { unfinishedSaveNotice } from '../utils/unfinishedSave';
import '../App.css';

interface Entity {
  entityId: string;
  bookIds?: string[];
  seriesIds?: string[];
}

interface NewSeriesModal {
  open: boolean;
  title: string;
  description: string;
  fromBookId?: string;
}

interface AddToSeriesModal {
  open: boolean;
  bookId: string;
  bookTitle: string;
}

interface DeleteBookModal {
  open: boolean;
  book: Book | null;
  deleteExclusiveAssets: boolean;
  exclusiveCharCount: number;
  exclusiveLocCount: number;
  confirmText: string;
  loading: boolean;
  fetchingAssets: boolean;
}

interface DeleteSeriesModal {
  open: boolean;
  series: Series | null;
  deleteBooks: boolean;
  deleteExclusiveAssets: boolean;
  exclusiveCharCount: number;
  exclusiveLocCount: number;
  confirmText: string;
  loading: boolean;
  fetchingAssets: boolean;
}

const EMPTY_DELETE_BOOK: DeleteBookModal = {
  open: false, book: null, deleteExclusiveAssets: false,
  exclusiveCharCount: 0, exclusiveLocCount: 0, confirmText: '', loading: false, fetchingAssets: false,
};

const EMPTY_DELETE_SERIES: DeleteSeriesModal = {
  open: false, series: null, deleteBooks: false, deleteExclusiveAssets: false,
  exclusiveCharCount: 0, exclusiveLocCount: 0, confirmText: '', loading: false, fetchingAssets: false,
};

function humanDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0)  return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7)   return `${days} days ago`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

function normalizeTitle(title: string): string {
  if (!title) return title;
  if (/\s/.test(title)) return title;
  const spaced = title.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  const LOWERCASE = new Set(['and', 'or', 'the', 'a', 'an', 'of', 'to', 'in', 'on', 'for', 'with']);
  return spaced.split(' ').map((word, i) => {
    if (i === 0) return word;
    if (LOWERCASE.has(word.toLowerCase())) return word.toLowerCase();
    return word;
  }).join(' ');
}

// A book's revisions, newest first by the date each one shows: when it was last saved
// (#1066). They arrive as a map, whose key order DynamoDB does not keep. One with no date
// goes last; ties go by name, also newest first (Revision 10 before Revision 2).
const savedAt = (r: Revision) => Date.parse(r.updatedAt || r.createdAt || '') || 0;
const revisionsByDate = (b: Book) => Object.entries(b.revisions ?? {})
  .sort(([nameA, revA], [nameB, revB]) => savedAt(revB) - savedAt(revA)
    || nameB.localeCompare(nameA, undefined, { numeric: true }));

const curRevName = (b: Book) => b.currentRevision || revisionsByDate(b)[0]?.[0] || '';
const curRevision = (b: Book) => b.revisions?.[curRevName(b)];

const COVER_GRADIENTS = [
  'linear-gradient(135deg, #196773 0%, #0d4a55 100%)',
  'linear-gradient(135deg, #145860 0%, #1a7a8a 100%)',
  'linear-gradient(135deg, #5CA383 0%, #196773 100%)',
  'linear-gradient(135deg, #2C3E50 0%, #196773 100%)',
];

function coverGradient(book: Book): string {
  const hash = book.entityId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return COVER_GRADIENTS[hash % COVER_GRADIENTS.length];
}

function getFirstUploaded(book: Book): string {
  const revVals = Object.values(book.revisions ?? {});
  const starts = revVals.map(r => r.createdAt ?? r.updatedAt).filter(Boolean).sort();
  return starts.length ? humanDate(starts[0]) : '—';
}

function getWorkingDuration(book: Book): string {
  const revVals = Object.values(book.revisions ?? {});
  const starts = revVals.map(r => r.createdAt ?? r.updatedAt).filter(Boolean).sort();
  const ends = revVals.map(r => r.updatedAt).filter(Boolean).sort();
  if (!starts.length) return '—';
  if (starts.length === 1 && ends.length === 1 && starts[0] === ends[0]) {
    return `started ${humanDate(starts[0])}`;
  }
  const diffMs = new Date(ends[ends.length - 1]).getTime() - new Date(starts[0]).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days < 1) return 'today';
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''}`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''}`;
}

// A save that didn't finish (#1145). The card, its word count and Download still show
// the last finished save; this says what became of the newer one.
const UnfinishedSaveNote: React.FC<{ book: Book }> = ({ book }) => {
  if (!book.unfinishedSave) return null;
  // No finalized revision: the book's first save has not finished.
  const text = unfinishedSaveNotice(book.unfinishedSave, new Date(), !book.currentRevision);
  return text ? <p className="unfinished-save-note">{text}</p> : null;
};

interface BookCardProps {
  book: Book;
  sequence?: number;
  seriesName?: string;
  progress?: WritingMetrics;
  onDetail: (book: Book) => void;
  onNavigate: (path: string) => void;
}

const BookCard: React.FC<BookCardProps> = ({ book, sequence, seriesName, progress, onDetail, onNavigate }) => {
  const cur       = curRevision(book);
  const revName   = curRevName(book);
  const nrev      = Object.keys(book.revisions ?? {}).length;
  const wordCount = cur?.wordCount ?? null;
  const displayTitle = normalizeTitle(book.title);
  const bookId = bareId(book.entityId);

  return (
    <div className="book-card advanced-data-panel clickable" onClick={() => onDetail(book)}>
      <div className="book-cover" style={{ background: coverGradient(book) }}>
        {sequence != null && <span className="sequence-badge">{sequence}</span>}
        {book.coverUrl
          ? <img src={book.coverUrl} alt={displayTitle} className="book-cover-img" />
          : <span className="book-cover-title">{displayTitle}</span>}
      </div>

      <div className="book-info">
        <h3 className="book-title-clamp" title={displayTitle}>{displayTitle}</h3>
        {seriesName && <div className="book-series-line">◆ {seriesName} · Book {sequence}</div>}

        <span className="revision-chip">{nrev > 0 ? revName : 'Draft'}</span>

        <div className="book-meta-row">
          {wordCount != null && wordCount > 0
            ? <span className="word-count">{wordCount.toLocaleString()} words</span>
            : <span className="word-count word-count--null" title="Word count pending">— words</span>}
          <span className="book-meta-sep">·</span>
          <span className="last-updated">{humanDate(cur?.updatedAt ?? book.updatedAt)}</span>
        </div>
        <UnfinishedSaveNote book={book} />

        {progress && (progress.streak > 0 || progress.week > 0 || progress.goal != null) && (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, fontSize: '0.78rem', color: '#8a9a97', flexWrap: 'wrap' }}
            title="Writing progress"
          >
            {progress.streak > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#c97a1e', fontWeight: 600 }} title={`${progress.streak}-day writing streak`}>
                <Flame size={12} /> {progress.streak}
              </span>
            )}
            {progress.week > 0 && (
              <span style={{ color: '#5CA383', fontWeight: 600 }}>+{progress.week.toLocaleString()} this week</span>
            )}
            {progress.goal != null && progress.goal > 0 && (
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                title={`Today: ${Math.max(0, progress.todaySigned).toLocaleString()} / ${progress.goal.toLocaleString()} goal`}
              >
                <span style={{ width: 44, height: 5, borderRadius: 3, background: '#eef2f1', overflow: 'hidden', display: 'inline-block' }}>
                  <span style={{ display: 'block', height: '100%', borderRadius: 3, background: progress.goalMetToday ? '#5CA383' : 'var(--teal-main, #196773)', width: `${Math.min(100, Math.round((Math.max(0, progress.todaySigned) / progress.goal) * 100))}%` }} />
                </span>
                {progress.goalMetToday && <span style={{ color: '#5CA383', fontWeight: 600 }}>✓</span>}
              </span>
            )}
          </div>
        )}

        <div className="book-actions" onClick={e => e.stopPropagation()}>
          <button className="btn-secondary small-btn" onClick={() => onNavigate(`/world?scope=book&id=${bookId}`)}>
            <Globe size={13} /> World Map
          </button>
          <button className="btn-secondary small-btn" onClick={() => onNavigate(`/storyboard?bookId=${bookId}`)}>
            <StickyNote size={13} /> Storyboard
          </button>
          <button className="btn-secondary small-btn" onClick={() => onNavigate(`/writing-dashboard?bookId=${bookId}`)}>
            <LineChart size={13} /> Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

const RevisionPanel: React.FC<{
  book: Book;
  seriesName?: string;
  sequence?: number;
  onClose: () => void;
  onNavigate: (p: string) => void;
  onDelete: (book: Book) => void;
}> = ({ book, seriesName, sequence, onClose, onNavigate, onDelete }) => {
  const cur    = curRevision(book);
  const revs   = revisionsByDate(book);
  const bookId = bareId(book.entityId);

  const downloadRevision = async (revName: string) => {
    try {
      const encodedRev = encodeURIComponent(revName);
      const res = await apiGet(`/books/${bookId}/revisions/${encodedRev}`);
      const data = await res.body.json() as { downloadUrl: string };
      window.open(data.downloadUrl, '_blank');
    } catch { /* silently fail — download not available */ }
  };

  return (
    <div className="side-panel side-panel--right" role="dialog" aria-label={`${book.title} revisions`}>
      <button className="side-panel-close" onClick={onClose} aria-label="Close"><X size={20} /></button>

      <header className="rev-panel-hero" style={{ background: coverGradient(book) }}>
        <div className="rev-panel-cover">
          {book.coverUrl
            ? <img src={book.coverUrl} alt="" />
            : <span>{normalizeTitle(book.title)}</span>}
        </div>
        <div>
          <h2>{normalizeTitle(book.title)}</h2>
          <span className="rev-panel-pill">
            {seriesName
              ? `◆ ${seriesName}${sequence != null ? ` · BOOK ${sequence}` : ''}`
              : (curRevName(book) || 'Draft')}
          </span>
        </div>
      </header>

      <div className="side-panel-scroll">
        <UnfinishedSaveNote book={book} />
        <p className="side-panel-section">
          Revision history <span>{revs.length} revision{revs.length !== 1 ? 's' : ''}</span>
        </p>
        <div className="rev-timeline">
          {revs.map(([name, r]) => {
            const isCurrent = name === book.currentRevision;
            return (
              <div key={name} className={`rev-row${isCurrent ? ' current' : ''}`}>
                <span className="rev-node" />
                <div className="rev-row-top">
                  <span className="rev-row-name">{name}</span>
                  {isCurrent && <span className="rev-current-tag">Current</span>}
                  <span className="rev-row-date">{humanDate(r.updatedAt)}</span>
                </div>
                <div className="rev-row-words">{(r.wordCount ?? 0).toLocaleString()} words</div>
                <div className="rev-row-actions">
                  <button className="rev-action" onClick={() => onNavigate(`/?open=${bookId}&rev=${encodeURIComponent(name)}`)}>
                    <ExternalLink size={12} /> Open in Chaptery
                  </button>
                  <button className="rev-action" onClick={() => downloadRevision(name)}>
                    <Download size={12} /> Download
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="side-panel-section" style={{ marginTop: '28px' }}>About this book</p>
        <div className="book-meta-grid">
          <div className="book-meta-stat">
            <span className="meta-label">First uploaded</span>
            <span className="meta-value">{getFirstUploaded(book)}</span>
          </div>
          <div className="book-meta-stat">
            <span className="meta-label">Time working</span>
            <span className="meta-value">{getWorkingDuration(book)}</span>
          </div>
          <div className="book-meta-stat">
            <span className="meta-label">Current words</span>
            <span className="meta-value">{(cur?.wordCount ?? 0).toLocaleString()}</span>
          </div>
          <div className="book-meta-stat">
            <span className="meta-label">Current revision</span>
            <span className="meta-value">{curRevName(book) || 'Draft — no revisions yet'}</span>
          </div>
        </div>
      </div>

      <footer className="side-panel-foot">
        {/* A book whose first save has not finished has no committed content to
            download; the server reports currentRevision as null for it. */}
        <button
          className="btn-secondary btn-secondary--filled"
          disabled={!curRevName(book)}
          onClick={() => downloadRevision(curRevName(book))}
        >
          <Download size={15} /> Download current copy
        </button>
        <button className="btn-secondary" onClick={() => { onClose(); onNavigate(`/world?scope=book&id=${bookId}`); }}>
          <Globe size={15} /> World Map
        </button>
        <button className="btn-secondary" onClick={() => { onClose(); onNavigate(`/storyboard?bookId=${bookId}`); }}>
          <StickyNote size={15} /> Storyboard
        </button>
        <button className="btn-secondary" onClick={() => { onClose(); onNavigate(`/writing-dashboard?bookId=${bookId}`); }}>
          <LineChart size={15} /> Writing Dashboard
        </button>
        <button className="btn-danger-ghost" onClick={() => { onClose(); onDelete(book); }}>
          <Trash2 size={15} /> Delete Book
        </button>
      </footer>
    </div>
  );
};

// accountId (the ID token's sub) tells the empty bookshelf whether this account was
// emailed its download link (#938). Required, so App.tsx can't silently drop it.
const BooksPage = ({ accountId }: { accountId: string | null }) => {
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[]>([]);
  const [series, setSeries] = useState<Series[]>([]);
  const [progress, setProgress] = useState<Record<string, WritingMetrics>>({});
  const [loading, setLoading] = useState(true);
  const [newSeriesModal, setNewSeriesModal] = useState<NewSeriesModal>({ open: false, title: '', description: '' });
  const [addToSeriesModal, setAddToSeriesModal] = useState<AddToSeriesModal>({ open: false, bookId: '', bookTitle: '' });
  const [deleteBookModal, setDeleteBookModal] = useState<DeleteBookModal>(EMPTY_DELETE_BOOK);
  const [deleteSeriesModal, setDeleteSeriesModal] = useState<DeleteSeriesModal>(EMPTY_DELETE_SERIES);
  const [revisionPanel, setRevisionPanel] = useState<{ book: Book | null; seriesName?: string; sequence?: number }>({ book: null });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The shelf itself could not be loaded: not an empty shelf, so no new-author panel.
  const [loadError, setLoadError] = useState('');

  useEffect(() => { fetchData(); }, []);

  // Load per-book writing progress for the library cards, non-blocking (cards
  // render immediately; progress fills in). One GET /books/{id}/writing-activity
  // per book — fine at library scale; a batch endpoint is a future optimization.
  useEffect(() => {
    if (books.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(books.map(async (b) => {
        const id = bareId(b.entityId);
        try {
          const res = await apiGet(`/books/${id}/writing-activity`);
          const d = (await res.body.json()) as unknown as WritingActivity;
          return [id, computeMetrics({ startTotal: d.startTotal ?? null, days: d.days ?? {}, dayStarts: d.dayStarts ?? {}, dailyWordGoal: d.dailyWordGoal ?? null })] as const;
        } catch {
          return [id, null] as const;
        }
      }));
      if (cancelled) return;
      const map: Record<string, WritingMetrics> = {};
      for (const [id, m] of results) if (m) map[id] = m;
      setProgress(map);
    })();
    return () => { cancelled = true; };
  }, [books]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [booksOp, seriesOp] = await Promise.all([apiGet('/books'), apiGet('/series')]);
      const booksData = (await booksOp.body.json()) as unknown as Book[];
      const seriesData = (await seriesOp.body.json()) as unknown as Series[];
      setBooks(Array.isArray(booksData) ? booksData : []);
      setSeries(Array.isArray(seriesData) ? seriesData : []);
    } catch (err) {
      console.error('Error fetching books:', err);
      setLoadError("We couldn't load your bookshelf. Refresh the page to try again.");
      setBooks([]);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async (): Promise<Entity[]> => {
    try {
      const res = await apiGet('/entities');
      const data = (await res.body.json()) as unknown as { characters: Entity[]; locations: Entity[] };
      return [...(data.characters || []), ...(data.locations || [])];
    } catch {
      return [];
    }
  };

  const handleCreateSeries = async () => {
    if (!newSeriesModal.title.trim()) return;
    setSaving(true);
    setError('');
    try {
      await apiPost('/series', {
        title: newSeriesModal.title.trim(),
        description: newSeriesModal.description.trim(),
        books: newSeriesModal.fromBookId
          ? [{ bookId: newSeriesModal.fromBookId, sequence: 1 }]
          : [],
      });
      setNewSeriesModal({ open: false, title: '', description: '' });
      await fetchData();
    } catch (e) {
      setError('Failed to create series. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddToSeries = async (seriesId: string) => {
    const targetSeries = series.find(s => s.entityId === seriesId);
    if (!targetSeries) return;
    const nextSeq = Math.max(0, ...(targetSeries.books || []).map(b => b.sequence)) + 1;
    setSaving(true);
    setError('');
    try {
      const rawSeriesId = seriesId.replace(/^SERIES#/, '');
      await apiPost(`/series/${rawSeriesId}/books`, {
        books: [{ bookId: addToSeriesModal.bookId, sequence: nextSeq }],
      });
      setAddToSeriesModal({ open: false, bookId: '', bookTitle: '' });
      await fetchData();
    } catch (e) {
      setError('Failed to add book to series.');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteBook = useCallback(async (book: Book) => {
    setDeleteBookModal({ ...EMPTY_DELETE_BOOK, open: true, book, fetchingAssets: true });
    const entities = await fetchEntities();
    const exclusive = entities.filter(e => {
      const bIds = e.bookIds || [];
      return bIds.length === 1 && bIds[0] === book.entityId && (e.seriesIds || []).length === 0;
    });
    const chars = exclusive.filter(e => e.entityId.startsWith('CHAR#')).length;
    const locs = exclusive.filter(e => e.entityId.startsWith('LOC#')).length;
    setDeleteBookModal(m => ({ ...m, fetchingAssets: false, exclusiveCharCount: chars, exclusiveLocCount: locs }));
  }, []);

  const handleDeleteBook = async () => {
    if (!deleteBookModal.book) return;
    setError('');
    setDeleteBookModal(m => ({ ...m, loading: true }));
    try {
      const id = bareId(deleteBookModal.book.entityId);
      const qs = deleteBookModal.deleteExclusiveAssets ? '?deleteExclusiveAssets=true' : '';
      await apiDel(`/books/${id}${qs}`);
      setDeleteBookModal(EMPTY_DELETE_BOOK);
      await fetchData();
    } catch {
      setDeleteBookModal(m => ({ ...m, loading: false }));
      setError('Failed to delete book. Please try again.');
    }
  };

  const openDeleteSeries = async (s: Series) => {
    setDeleteSeriesModal({ ...EMPTY_DELETE_SERIES, open: true, series: s, fetchingAssets: true });
    const entities = await fetchEntities();
    const seriesBookIds = (s.books || []).map(b => b.bookId);
    const exclusive = entities.filter(e => {
      const sIds = e.seriesIds || [];
      const bIds = e.bookIds || [];
      return sIds.length === 1 && sIds[0] === s.entityId &&
        (bIds.length === 0 || bIds.every(id => seriesBookIds.includes(id)));
    });
    const chars = exclusive.filter(e => e.entityId.startsWith('CHAR#')).length;
    const locs = exclusive.filter(e => e.entityId.startsWith('LOC#')).length;
    setDeleteSeriesModal(m => ({ ...m, fetchingAssets: false, exclusiveCharCount: chars, exclusiveLocCount: locs }));
  };

  const handleDeleteSeries = async () => {
    if (!deleteSeriesModal.series) return;
    setError('');
    setDeleteSeriesModal(m => ({ ...m, loading: true }));
    try {
      const id = deleteSeriesModal.series.entityId.replace(/^SERIES#/, '');
      const params = new URLSearchParams();
      if (deleteSeriesModal.deleteBooks) params.set('deleteBooks', 'true');
      if (deleteSeriesModal.deleteExclusiveAssets) params.set('deleteExclusiveAssets', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      await apiDel(`/series/${id}${qs}`);
      setDeleteSeriesModal(EMPTY_DELETE_SERIES);
      await fetchData();
    } catch {
      setDeleteSeriesModal(m => ({ ...m, loading: false }));
      setError('Failed to delete series. Please try again.');
    }
  };

  // curRevision() falls back to the newest revision when currentRevision is null, which
  // is a real state now that the server nulls it for a book whose first save has not
  // finished. Indexing revisions with the raw field would break on that book.
  const getWordCount = (book: Book) => curRevision(book)?.wordCount ?? null;

  const renderBookCard = useCallback((book: Book, sequence?: number, seriesName?: string) => (
    <BookCard
      key={book.entityId}
      book={book}
      sequence={sequence}
      seriesName={seriesName}
      progress={progress[bareId(book.entityId)]}
      onDetail={b => setRevisionPanel({ book: b, seriesName, sequence })}
      onNavigate={navigate}
    />
  ), [navigate, progress]);

  const renderSeriesStack = (s: Series) => {
    const bookMap = new Map(books.map(b => [b.entityId, b]));
    const seriesBooks = (s.books ?? [])
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map(entry => ({ book: bookMap.get(entry.bookId), sequence: entry.sequence }))
      .filter(entry => entry.book != null) as { book: Book; sequence: number }[];

    const totalWords = seriesBooks.reduce((sum, { book }) => sum + (getWordCount(book) ?? 0), 0);
    const earliestRevisionDate = seriesBooks
      .flatMap(({ book }) => Object.values(book.revisions ?? {}).map(r => r.updatedAt))
      .filter(Boolean)
      .sort()[0];

    return (
      <div key={s.entityId} className="series-container">
        <div className="series-stack-wrapper series-stack-wrapper--v2">
          <div className="series-visual-stack series-visual-stack--v2">
            {seriesBooks.slice(0, 3).map(({ book }, idx) => (
              <div key={`stack-${book.entityId}`} className={`stack-item stack-item-${idx}`}>
                {book.coverUrl
                  ? <img src={book.coverUrl} alt={book.title} className="stack-cover-img" />
                  : <span className="book-icon-small">📖</span>
                }
              </div>
            ))}
            <div className="series-overlay-info series-overlay-info--v2">
              <Layers size={18} />
              <span>{seriesBooks.length} Books</span>
            </div>
          </div>
          <div className="series-details series-details--v2">
            <h2 className="series-title">{s.title}</h2>
            {s.description && <p className="series-desc">{s.description}</p>}
            <div className="series-stats">
              <span><strong className="series-stat-num">{seriesBooks.length}</strong> book{seriesBooks.length !== 1 ? 's' : ''}</span>
              {totalWords > 0 && (
                <>
                  <span className="series-stat-divider">·</span>
                  <span><strong className="series-stat-num">{totalWords.toLocaleString()}</strong> words</span>
                </>
              )}
              {earliestRevisionDate && (
                <>
                  <span className="series-stat-divider">·</span>
                  <span>started {humanDate(earliestRevisionDate)}</span>
                </>
              )}
            </div>
            <div className="series-actions">
              <button
                className="btn-secondary small-btn"
                onClick={() => navigate(`/world?scope=series&id=${s.entityId.replace(/^SERIES#/, '')}`)}
              >
                <Globe size={13} /> World Map
              </button>
              <button
                className="btn-danger-ghost small-btn series-delete-btn"
                onClick={() => openDeleteSeries(s)}
                title="Delete series"
              >
                <Trash2 size={13} /> Delete Series
              </button>
            </div>
          </div>
        </div>
        <div className="series-books-grid">
          {seriesBooks.map(({ book, sequence }) => renderBookCard(book, sequence, s.title))}
        </div>
      </div>
    );
  };

  const seriesBookIds = new Set(series.flatMap(s => (s.books ?? []).map(b => b.bookId)));
  const standaloneBooks = books.filter(b => !seriesBookIds.has(b.entityId));

  if (loading) {
    return <div className="books-page container loading-container"><p>Loading your bookshelf...</p></div>;
  }

  const bookDeleteReady = deleteBookModal.book !== null &&
    deleteBookModal.confirmText === deleteBookModal.book.title &&
    !deleteBookModal.loading;

  const seriesDeleteReady = deleteSeriesModal.series !== null &&
    deleteSeriesModal.confirmText === deleteSeriesModal.series.title &&
    !deleteSeriesModal.loading;

  // The modals mount in both branches below.
  const modals = (
    <>
      {/* New Series Modal */}
      {newSeriesModal.open && (
        <div className="modal-overlay" onClick={() => setNewSeriesModal({ open: false, title: '', description: '' })}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <h2>New Series</h2>
            <p className="modal-subtitle">Create a collection to group related books together.</p>
            <div className="form-group">
              <label>Series Title <span className="required-marker">*</span></label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. The Chronicles of Chaptery"
                value={newSeriesModal.title}
                onChange={e => setNewSeriesModal(m => ({ ...m, title: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Description <span className="optional">(optional)</span></label>
              <textarea
                className="form-input"
                rows={3}
                placeholder="A brief description of the series..."
                value={newSeriesModal.description}
                onChange={e => setNewSeriesModal(m => ({ ...m, description: e.target.value }))}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setNewSeriesModal({ open: false, title: '', description: '' })}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateSeries} disabled={saving || !newSeriesModal.title.trim()}>
                {saving ? 'Creating...' : 'Create Series'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Series Modal */}
      {addToSeriesModal.open && (
        <div className="modal-overlay" onClick={() => setAddToSeriesModal({ open: false, bookId: '', bookTitle: '' })}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <h2>Add to Series</h2>
            <p className="modal-subtitle">"{addToSeriesModal.bookTitle}"</p>
            {series.length === 0 ? (
              <>
                <p>You don't have any series yet.</p>
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={() => setAddToSeriesModal({ open: false, bookId: '', bookTitle: '' })}>Cancel</button>
                  <button className="btn-primary" onClick={() => {
                    setAddToSeriesModal({ open: false, bookId: '', bookTitle: '' });
                    setNewSeriesModal({ open: true, title: '', description: '', fromBookId: addToSeriesModal.bookId });
                  }}>Create New Series</button>
                </div>
              </>
            ) : (
              <>
                <p className="modal-subtitle">Choose an existing series or create a new one:</p>
                <div className="series-pick-list">
                  {series.map(s => (
                    <button key={s.entityId} className="series-pick-item" onClick={() => handleAddToSeries(s.entityId)} disabled={saving}>
                      <Layers size={16} />
                      <span>{s.title}</span>
                      <span className="series-pick-count">{(s.books || []).length} books</span>
                    </button>
                  ))}
                </div>
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={() => setAddToSeriesModal({ open: false, bookId: '', bookTitle: '' })}>Cancel</button>
                  <button className="btn-secondary" onClick={() => {
                    setAddToSeriesModal({ open: false, bookId: '', bookTitle: '' });
                    setNewSeriesModal({ open: true, title: '', description: '', fromBookId: addToSeriesModal.bookId });
                  }}>+ New Series</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Book Modal */}
      {deleteBookModal.open && deleteBookModal.book && (
        <div className="modal-overlay">
          <div className="modal-panel modal-danger" onClick={e => e.stopPropagation()}>
            <div className="danger-modal-header">
              <Trash2 size={20} />
              <h2>Delete "{deleteBookModal.book.title}"?</h2>
            </div>
            <p className="danger-warning">This cannot be undone. The book and all its uploaded files will be permanently deleted.</p>

            {deleteBookModal.fetchingAssets ? (
              <p className="modal-loading">Checking for associated characters and locations...</p>
            ) : (deleteBookModal.exclusiveCharCount > 0 || deleteBookModal.exclusiveLocCount > 0) && (
              <div className="asset-disposition">
                <p className="asset-disposition-label">
                  This book has{' '}
                  {deleteBookModal.exclusiveCharCount > 0 && <strong>{deleteBookModal.exclusiveCharCount} character{deleteBookModal.exclusiveCharCount !== 1 ? 's' : ''}</strong>}
                  {deleteBookModal.exclusiveCharCount > 0 && deleteBookModal.exclusiveLocCount > 0 && ' and '}
                  {deleteBookModal.exclusiveLocCount > 0 && <strong>{deleteBookModal.exclusiveLocCount} location{deleteBookModal.exclusiveLocCount !== 1 ? 's' : ''}</strong>}
                  {' '}that aren't used anywhere else. What should happen to them?
                </p>
                <div className="disposition-options">
                  <label className="disposition-option">
                    <input
                      type="radio"
                      name="bookAssetDisposition"
                      checked={!deleteBookModal.deleteExclusiveAssets}
                      onChange={() => setDeleteBookModal(m => ({ ...m, deleteExclusiveAssets: false }))}
                    />
                    <span>Keep characters and locations</span>
                  </label>
                  <label className="disposition-option disposition-option-danger">
                    <input
                      type="radio"
                      name="bookAssetDisposition"
                      checked={deleteBookModal.deleteExclusiveAssets}
                      onChange={() => setDeleteBookModal(m => ({ ...m, deleteExclusiveAssets: true }))}
                    />
                    <span>Delete characters and locations that belong only to this book</span>
                  </label>
                </div>
              </div>
            )}

            <div className="form-group confirm-input-group">
              <label>Type <strong>{deleteBookModal.book.title}</strong> to confirm:</label>
              <input
                type="text"
                className="form-input"
                placeholder={deleteBookModal.book.title}
                value={deleteBookModal.confirmText}
                onChange={e => setDeleteBookModal(m => ({ ...m, confirmText: e.target.value }))}
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteBookModal(EMPTY_DELETE_BOOK)} disabled={deleteBookModal.loading}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDeleteBook} disabled={!bookDeleteReady}>
                {deleteBookModal.loading ? 'Deleting...' : 'Delete Book'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Series Modal */}
      {deleteSeriesModal.open && deleteSeriesModal.series && (
        <div className="modal-overlay">
          <div className="modal-panel modal-danger" onClick={e => e.stopPropagation()}>
            <div className="danger-modal-header">
              <Trash2 size={20} />
              <h2>Delete "{deleteSeriesModal.series.title}"?</h2>
            </div>
            <p className="danger-warning">This cannot be undone. The series grouping will be permanently deleted.</p>

            <div className="asset-disposition">
              <p className="asset-disposition-label">What should happen to the <strong>{deleteSeriesModal.series.books.length} book{deleteSeriesModal.series.books.length !== 1 ? 's' : ''}</strong> in this series?</p>
              <div className="disposition-options">
                <label className="disposition-option">
                  <input
                    type="radio"
                    name="seriesBookDisposition"
                    checked={!deleteSeriesModal.deleteBooks}
                    onChange={() => setDeleteSeriesModal(m => ({ ...m, deleteBooks: false }))}
                  />
                  <span>Keep books as standalone books</span>
                </label>
                <label className="disposition-option disposition-option-danger">
                  <input
                    type="radio"
                    name="seriesBookDisposition"
                    checked={deleteSeriesModal.deleteBooks}
                    onChange={() => setDeleteSeriesModal(m => ({ ...m, deleteBooks: true }))}
                  />
                  <span>Delete all books in this series</span>
                </label>
              </div>
            </div>

            {deleteSeriesModal.fetchingAssets ? (
              <p className="modal-loading">Checking for associated characters and locations...</p>
            ) : (deleteSeriesModal.exclusiveCharCount > 0 || deleteSeriesModal.exclusiveLocCount > 0) && (
              <div className="asset-disposition">
                <p className="asset-disposition-label">
                  This series has{' '}
                  {deleteSeriesModal.exclusiveCharCount > 0 && <strong>{deleteSeriesModal.exclusiveCharCount} character{deleteSeriesModal.exclusiveCharCount !== 1 ? 's' : ''}</strong>}
                  {deleteSeriesModal.exclusiveCharCount > 0 && deleteSeriesModal.exclusiveLocCount > 0 && ' and '}
                  {deleteSeriesModal.exclusiveLocCount > 0 && <strong>{deleteSeriesModal.exclusiveLocCount} location{deleteSeriesModal.exclusiveLocCount !== 1 ? 's' : ''}</strong>}
                  {' '}that aren't used anywhere else. What should happen to them?
                </p>
                <div className="disposition-options">
                  <label className="disposition-option">
                    <input
                      type="radio"
                      name="seriesAssetDisposition"
                      checked={!deleteSeriesModal.deleteExclusiveAssets}
                      onChange={() => setDeleteSeriesModal(m => ({ ...m, deleteExclusiveAssets: false }))}
                    />
                    <span>Keep characters and locations</span>
                  </label>
                  <label className="disposition-option disposition-option-danger">
                    <input
                      type="radio"
                      name="seriesAssetDisposition"
                      checked={deleteSeriesModal.deleteExclusiveAssets}
                      onChange={() => setDeleteSeriesModal(m => ({ ...m, deleteExclusiveAssets: true }))}
                    />
                    <span>Delete characters and locations that belong only to this series</span>
                  </label>
                </div>
              </div>
            )}

            <div className="form-group confirm-input-group">
              <label>Type <strong>{deleteSeriesModal.series.title}</strong> to confirm:</label>
              <input
                type="text"
                className="form-input"
                placeholder={deleteSeriesModal.series.title}
                value={deleteSeriesModal.confirmText}
                onChange={e => setDeleteSeriesModal(m => ({ ...m, confirmText: e.target.value }))}
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteSeriesModal(EMPTY_DELETE_SERIES)} disabled={deleteSeriesModal.loading}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDeleteSeries} disabled={!seriesDeleteReady}>
                {deleteSeriesModal.loading ? 'Deleting...' : 'Delete Series'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revision Panel */}
      {revisionPanel.book && (
        <>
          <div className="side-panel-scrim" onClick={() => setRevisionPanel({ book: null })} />
          <RevisionPanel
            book={revisionPanel.book}
            seriesName={revisionPanel.seriesName}
            sequence={revisionPanel.sequence}
            onClose={() => setRevisionPanel({ book: null })}
            onNavigate={navigate}
            onDelete={openDeleteBook}
          />
        </>
      )}
    </>
  );

  // A fresh account (#1130): nothing on the shelf at all, and it loaded. The start panel
  // is the whole page, with the app beside it; the page's own header waits for a book.
  const fresh = books.length === 0 && series.length === 0 && !loadError;
  if (fresh) {
    return (
      <div className="books-page container">
        {error && <div className="error-banner">{error}</div>}
        <DesktopAppNote accountId={accountId} variant="hero" headingLevel={1} />
        {modals}
      </div>
    );
  }

  return (
    <div className="books-page container">
      <header className="page-header">
        <div className="header-flex">
          <div>
            <h1>My Bookshelf</h1>
            <p>Manage your books, revisions, and series collections.</p>
          </div>
          {/* A fresh shelf is the start hero above (#1130); here the shelf is only empty
              when it failed to load, so there is nothing to add a series to yet. */}
          {(books.length > 0 || series.length > 0) && (
            <button
              className="btn-primary"
              onClick={() => setNewSeriesModal({ open: true, title: '', description: '' })}
            >
              + New Series
            </button>
          )}
        </div>
      </header>

      {loadError && <div className="error-banner">{loadError}</div>}
      {error && <div className="error-banner">{error}</div>}

      <div className="bookshelf-content">
        {series.length > 0 && (
          <section className="series-section">
            {series.map(s => renderSeriesStack(s))}
          </section>
        )}

        {books.length === 0 && !loadError && (
          <section className="empty-bookshelf-section">
            <div className="advanced-data-panel empty-bookshelf-panel">
              <h3>Your bookshelf is empty</h3>
              <DesktopAppNote accountId={accountId} />
            </div>
          </section>
        )}

        {standaloneBooks.length > 0 && (
          <section className="standalone-section">
            <h2 className="section-title">Standalone Books</h2>
            <div className="books-gallery books-gallery--standalone">
              {standaloneBooks.map(book => renderBookCard(book))}
            </div>
          </section>
        )}
      </div>

      {modals}
    </div>
  );
};

export default BooksPage;
